import {IAttributeData, IIntegerRange, LengthUnit} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {MediaTypeCategory} from '@colibrio/colibrio-reader-framework/colibrio-core-io-base';
import {ILocator, ILocatorData, ISimpleLocatorData, LocatorFactory} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import {
    ContainerContentBlockType,
    IEngineEventMediaResourceData,
    IKeyboardEngineEvent,
    IMouseEngineEvent,
    INavigationEndedEngineEvent,
    INavigationIntentEngineEvent,
    INavigationStartedEngineEvent,
    IPointerEngineEvent,
    IReaderPublication,
    IReaderViewEngineEvent,
    IReaderViewGotoOptions,
    ISelectionChangedEngineEvent,
    ISwipeNavigationGestureProgressEngineEventData,
    ITransformData,
    ITransformRectZoomOptions,
    ReaderViewGestureType,
    SyncMediaRangePlaybackRemoveReason,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {IntRange} from '../utils/IntRange';
import {IVanillaReaderOptionsDataStore} from '../VanillaReaderDataStore/IVanillaReaderOptionsDataStore';
import {VanillaReaderAudiobookPlayer} from './VanillaReaderAudiobookPlayer';
import {VanillaReaderMediaPlayer} from './VanillaReaderMediaPlayer';
import {
    IVanillaReaderBookmarkData,
    IVanillaReaderContentPositionData,
    IVanillaReaderDocumentLandmark,
    IVanillaReaderEscapableContentBlockContext,
    IVanillaReaderEscapableContentBlockDataContext,
    IVanillaReaderHighlightData,
    IVanillaReaderInitialOptions,
    IVanillaReaderNavigationAction,
    IVanillaReaderOptionsData,
    IVanillaReaderPublicationLandmarkCollection,
    IVanillaReaderPublicationSearchResultItem,
    IVanillaReaderPublicationStyleOptions,
    IVanillaReaderReadingProgressionTimeline,
    IVanillaReaderReadingProgressionTimelineData,
    IVanillaReaderSyncMediaOptions,
    IVanillaReaderTransformData,
    IVanillaReaderUserProfileData,
    IVanillaSyncMediaPlaybackStateData,
    VanillaReaderNavigationType,
    VanillaReaderPublicationLandmarkType,
    VanillaReaderRendererNames
} from './VanillaReaderModel';
import {
    IEpubReaderDocument,
    IEpubReaderPublicationOptions
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub";
import {
    IPdfReaderPublicationOptions
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-pdf";
import {
    IWpAudiobookReaderPublicationOptions
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-wp-audiobook";
import {VanillaReaderUiIntentController} from "./VanillaReaderUiIntentController";
import {VanillaReaderReadingSystem} from "./VanillaReaderReadingSystem";
import {VanillaReaderPublication} from "./VanillaReaderPublication";
import {IVanillaReaderPublicationSearch} from "./IVanillaReaderPublicationSearch";
import {VanillaReaderView} from "./VanillaReaderView";
import {CustomEventListener, VanillaReaderEventBus} from "../VanillaReaderEventBus/VanillaReaderEventBus";
import {VanillaReaderShelfItem} from "../VanillaReaderBookshelf/VanillaReaderShelfItem";
import {VanillaReaderPublicationLandmarkCollection} from "./VanillaReaderPublicationLandmarkCollection";
import {VanillaReaderProgressionTimeline} from "./VanillaReaderProgressionTimeline";
import {VanillaReaderPublicationSearch} from "./VanillaReaderPublicationSearch";
import {
    IVanillaReaderAppEvent_APP_ONLINE_STATE_CHANGED,
    IVanillaReaderAppEvent_BOOKMARK_ADDED,
    IVanillaReaderAppEvent_BOOKMARK_DELETED,
    IVanillaReaderAppEvent_HIGHLIGHT_ADDED,
    IVanillaReaderAppEvent_HIGHLIGHT_DELETED,
    IVanillaReaderAppEvent_HIGHLIGHT_UPDATED,
    IVanillaReaderAppEvent_MEDIA_OBJECT_CLICKED
} from "../VanillaReaderEventBus/IVanillaReaderAppEventTypeMap";
import {VanillaReaderUtils} from "./VanillaReaderUtils";
import {VanillaReaderAudiobookReaderView} from "./VanillaReaderAudiobookReaderView";
import {ISpeechSynthesisVoiceCollection, WebSpeechUtils} from "../utils/WebSpeechUtils";
import { EpubCfiFragmentSelectorParser } from '@colibrio/colibrio-reader-framework/colibrio-core-selector-epubcfi';
import { PdfFragmentSelectorParser } from '@colibrio/colibrio-reader-framework/colibrio-core-selector-pdf';
import { SelectorFactory } from '@colibrio/colibrio-reader-framework/colibrio-core-selector-base';
import { PublicationType } from '@colibrio/colibrio-reader-framework/colibrio-core-publication-base';
import { XhtmlParser } from '@colibrio/colibrio-reader-framework/colibrio-core-xml';
import { AmbientAudioMediaPlayer } from '../utils/ambient-audio/AmbientAudioMediaPlayer';
import { IEpubPublication } from '@colibrio/colibrio-reader-framework/colibrio-core-publication-epub';

/**
 *
 * # VanillaReader
 *
 *  ## RESPONSIBILITIES
 *
 * VanillaReader is where all the fun Colibrio Reader Framework action takes place!
 *
 * This class has no UI related code, if you are looking for such things they are in the
 * `VanillaReaderUI` module in this project.
 *
 * The UI does not call this class directly. All other modules use the public API in the VanillaReaderAPI class.
 *
 *
 * ## NAMING CONVENTIONS
 *
 * All classes and other types that are specific to the Vanilla Reader application are prefixed with "VanillaReader".
 * Instances of Colibrio Framework classes are prefixed with "_colibrio". This will make it easier for you to see
 * the boundaries between app code and framework types.
 *
 *
 * ## EVENT SYSTEM
 *
 * To keep a clear separation between the application logic in the VanillaReader module and the UI etc. all communication
 * between modules are done using the `VanillaReaderEventBus`.
 *
 * When some other module, such as the VanillaReaderAPI, has something it wants the VanillaReader (and thus the Colibrio
 * Reader Framework) to do, it will call one of its methods. However, when the `VanillaReader` has updated its state,
 * it will dispatch an event that the application state has changed and sends along the updated state data as payload.
 *
 * The general convention is that other modules call methods on VanillaReader to perform actions and that the VanillaReader
 * responds with updated data using events.
 *
 *      |-----------------------|                   |-----------------------|
 *      |                       |   -- method ->    |                       |
 *      |    Other module       |                   |     VanillaReader     |
 *      |                       |   <- event --     |                       |
 *      |-----------------------|                   |-----------------------|
 *
 * All events have their own types. All the event names, and the type interfaces, are found in the `IVanillaReaderAppEventTypeMap`
 * interface.
 *
 *
 * ## STATE MANAGEMENT
 *
 * ### Colibrio runtime state
 * In the Colibrio Reader Framework the different components, such as ReaderViews and ContentPositionTimeline, manage
 * their own state. Almost all data types in the Colibrio Reader Framework are serializable to JSON. This means that it
 * is easy to serialize runtime state, and also to restore it.
 *
 * ### Vanilla Reader state
 * The Vanilla Reader application state, with data about user customization options, bookmarks etc. is persisted to
 * various data stores such as the `VanillaReaderOptionsIndexedDbDataStore` and the `VanillaReaderBookmarkIndexedDbDataStore`.
 * All data store types and their implementations can be found in the `src/VanillaDataStore/` folder.
 *
 *
 * ## MULTI FORMAT SUPPORT
 *
 * Vanilla Reader supports both traditional text based publications as well audiobooks. This implementation strategy is
 * mainly chosen in order to make the Vanilla Reader your one-stop-shop to check out all basic Colibrio Framework
 * features. In a real world application you should probably separate the audiobook implementation more clearly from the
 * EPUB and PDF implementation.
 *
 * The main difference between traditional ebooks and audiobooks is of course that the audiobook does not necessarily
 * render anything to the screen, and therefore do not have a `IReaderView` instance.
 * Another difference is that the audiobooks do not have a `ContentPositionTimeline`, but relies instead on the
 * `ISyncMediaTimeline` (the same type that is used by the TTS and Media Overlay features).
 *
 * To make things easier for the UI to work across the different formats without too many additional forks in its code,
 * the differences between the `ContentPositionTimeline` and the `ISyncMediaTimeline` have been "hidden" behind the
 * `IVanillaReaderReadingProgressionTimeline` interface. Check out the `_createReadingProgressionTimeline` method for
 * more details.
 *
 * To see where the code paths differ between ebooks and audiobooks start in the `_onAfterPublicationLoaded` method.
 * Additionally, you can search for usages of the `vanillaPublication.isAudiobook` property, and also the "type predicate"
 * `_isWpAudiobookReaderPublication`.
 *
 */

export class VanillaReader {
    vanillaReadingSystem: VanillaReaderReadingSystem;
    vanillaReadingProgressionTimeLine: IVanillaReaderReadingProgressionTimeline | undefined = undefined;
    vanillaMediaPlayer: VanillaReaderMediaPlayer | undefined = undefined;
    vanillaAudiobookPlayer: VanillaReaderAudiobookPlayer | undefined = undefined;
    vanillaOptionsDataStore: IVanillaReaderOptionsDataStore;
    vanillaPublication: VanillaReaderPublication | undefined;
    vanillaPublicationSearch: IVanillaReaderPublicationSearch | undefined;
    vanillaReaderView: VanillaReaderView | undefined;
    vanillaAudiobookView: VanillaReaderAudiobookReaderView | undefined;
    ambientMediaPlayer: AmbientAudioMediaPlayer | undefined;

    private _colibrioReaderPublication: IReaderPublication | void = undefined;

    private _isOffline: boolean = false;
    private _isLoaded: boolean = false;
    private _shouldFocusOnReadingPosition: boolean = true;

    constructor(
        private _readerViewElement: HTMLElement | undefined,
        // This is the license key that has been provided to you by Colibrio.
        colibrioLicenseApiKey: string,
        // User profile data is used to tag reading sessions that are sent to the Colibrio License Server with a unique token.
        // See `fetchOrCreateUserProfile()` in index.ts for our sample implementation.
        private _userProfileData: IVanillaReaderUserProfileData,
        // Initial configuration options retrieved by the calling context (index.ts)
        private _initialOptions: IVanillaReaderInitialOptions,
        optionsDataStore: IVanillaReaderOptionsDataStore
    ) {

        new VanillaReaderUiIntentController(this);

        this.vanillaOptionsDataStore = optionsDataStore;
        this.vanillaReadingSystem = new VanillaReaderReadingSystem(colibrioLicenseApiKey, this._userProfileData);
        this._setUpEventBusEventHandlers();
        this._setUpComponentCallbacks();

        // Let's report to all that are listening that we are all set to open books!
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('APP_LOADED', {
            initialOptions: this._initialOptions,
        }));

        /**
         *
         * What happens now?
         *
         * Now the ReaderSystemEngine is up and running, but we still have not loaded any publication. That happens in
         * one of the following methods:
         *
         * - loadPublicationFromUrl()
         * - loadPublicationFromStore()
         * - loadPublicationFromFile()
         *
         * Once the publication has started loading by the `VanillaReaderReadingSystem` (which in turn of course uses the
         * Colibrio Reader Framework `ReadingSystemEngine`) things continue in `_onAfterPublicationLoaded`. Go check it out!
         *
         * */

    }

    setReaderViewElement(readerViewElement: HTMLElement): void {
        this._readerViewElement = readerViewElement;
    }

    public get readerViewElement(): HTMLElement {
        if(!this._readerViewElement) {
            throw new Error('VanillaReader.readerViewElement has not been set.');
        }
        return this._readerViewElement;
    }

    /*
    *
    * This method serializes all configuration options to be persisted between application sessions.
    *
    * */
    serializeToOptionsData(): IVanillaReaderOptionsData {
        let options: IVanillaReaderOptionsData = {
            dateCreated: Date.now(),
            viewOptions: undefined,
            syncMediaOptions: undefined,
        };

        options.viewOptions = this.vanillaReaderView?.getCurrentVanillaReaderViewOptionsState();
        options.syncMediaOptions = this.vanillaMediaPlayer?.getPlaybackOptions();

        return options;
    }

    /**
     *
     * This property turns on/off the accessibility feature that helps a Screen Reader focus on the current reading
     * position. Focusing is done, as the name suggests, by calling `HTMLElement.focus()` near the reading position.
     * We recommend that you turn this on as a default.
     *
     * NOTE:
     *
     * A consequence of setting this to true is that a focus marker will be rendered in many books, so you might want to
     * have an option to toggle this in the UI.
     *
     * */
    public get shouldFocusOnReadingPosition(): boolean {
        return this._shouldFocusOnReadingPosition;
    }

    public set shouldFocusOnReadingPosition(value: boolean) {
        this._shouldFocusOnReadingPosition = value;
    }

    async getReadingPositionData(): Promise<IVanillaReaderContentPositionData | undefined> {
        return this.vanillaReaderView?.getReadingPositionData();
    }

    /*
    *
    * It is very important for the whole application to be aware of any changes to internet connectivity. This property
    * is set by the calling context (index.ts in our case) to signal this. The method itself determines if the new state
    * really is different from the current state, and if it is an app wide event is sent.
    *
    */
    public set isOffline(state: boolean) {

        if (this._isOffline !== state) {
            const event = VanillaReaderEventBus.createEvent('APP_ONLINE_STATE_CHANGED', {isOnline: !state});
            VanillaReaderEventBus.dispatchEvent(event);
        }
        this._isOffline = state;
    }

    get isOffline(): boolean {
        return this._isOffline;
    }

    public set isLoaded(state: boolean){
        this._isLoaded = state;
    }

    get isLoaded(): boolean {
        return this._isLoaded;
    }

    /**
     *
     * This method takes in a URL as a string and tries to load a publication from it. "Under the hood" it uses the
     * Colibrio `ReadingSystemEngine`, so have a peek at the `VanillaReaderReadingSystem.loadPublicationFromUrl()`
     *
     * @param publicationUrl - A string URL that points to a publication resource file
     * @param shelfItem - Shelf item is what actual represents the "used" book. It manages all the books state such as notes, bookmarks, reading positions etc.
     * @param size - Optional. The byte size of publication resource. This is only necessary if you can not make HEAD requests to your file server.
     * @param initialLocator - Optional. A publication location to navigate to after the publication has been loaded.
     * @param storeOnDevice - Optional. Should the `IRandomAccessDataSource` instance used when loading the publication use IndexedDB for long term caching?
     * @return void
     * */
    async loadPublicationFromUrl(
        publicationUrl: string,
        shelfItem: VanillaReaderShelfItem,
        size?: number | undefined,
        initialLocator?: string | undefined,
        storeOnDevice: boolean = false,
    ): Promise<void> {
        this._colibrioReaderPublication = await this.vanillaReadingSystem.loadPublicationFromUrl(publicationUrl, shelfItem, size, storeOnDevice).catch((reason) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_LOAD_FAILED', {errorMessage: reason}));
        });

        if (!this._colibrioReaderPublication) {
            throw new DOMException('VanillaReader.loadPublicationFromUrl(), Failed to load publication');
        }

        let pathName = new URL(publicationUrl).pathname;
        let fileName = pathName.slice(pathName.lastIndexOf('/') + 1);

        await this._onAfterPublicationLoaded(fileName, publicationUrl, this._colibrioReaderPublication, shelfItem, initialLocator);
    }


    async loadPublicationFromAwsLambda(
        publicationUrl: string,
        urlResolver: Function,
        shelfItem: VanillaReaderShelfItem,
        size?: number | undefined,
        initialLocator?: string | undefined,
        storeOnDevice: boolean = false,
    ): Promise<void> {
        this._colibrioReaderPublication = await this.vanillaReadingSystem.loadPublicationFromAwsLambda(publicationUrl, urlResolver, shelfItem, size, storeOnDevice).catch((reason) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_LOAD_FAILED', {errorMessage: reason}));
        });

        if (!this._colibrioReaderPublication) {
            throw new DOMException('VanillaReader.loadPublicationFromAwsLambda(), Failed to load publication');
        }

        let pathName = new URL(publicationUrl).pathname;
        let fileName = pathName.slice(pathName.lastIndexOf('/') + 1);
        await this._onAfterPublicationLoaded(fileName, publicationUrl, this._colibrioReaderPublication, shelfItem, initialLocator);
    }

    /**
     *
     * This method takes in a file name and a source URI to identify the origin of the file and tries to load the publication
     * from using the `IVanillaReaderStreamedResourceStorage` storage client. It uses two different methods to load data,
     * for `https:` URIs it uses `VanillaReaderReadingSystem.loadPublicationFromUrl()` and for `file:` based URIs it uses
     * `VanillaReaderReadingSystem.loadPublicationFromArrayBuffer()`
     *
     * @param fileName - A publication resource file name as a string
     * @param fileSourceUri - The URI used to load the publication as a string
     * @param shelfItem - Shelf item is what actual represents the "used" book. It manages all the books state such as notes, bookmarks, reading positions etc.
     * @param initialLocator - Optional. A publication location to navigate to after the publication has been loaded.
     * @param storeOnDevice - Optional. Should the `IRandomAccessDataSource` instance used when loading the publication use IndexedDB for long term caching?
     * @return void
     * */
    async loadPublicationFromStore(
        fileName: string,
        fileSourceUri: string,
        shelfItem: VanillaReaderShelfItem,
        initialLocator?: string | undefined,
        storeOnDevice: boolean = false,
    ): Promise<void> {
        if (shelfItem) {

            let storedResource = await shelfItem.fetchStoredResource();

            if (storedResource) {
                // If the fileSourceUri parameter is provided this is a URL
                if (fileSourceUri?.startsWith('https://')) {
                    return this.loadPublicationFromUrl(fileSourceUri, shelfItem, storedResource.size, initialLocator, storeOnDevice);
                } else if ((fileSourceUri?.startsWith('file://'))) {
                    // According to `fileSourceUri` string, this is a file resource. Files opened from the file system
                    // are stored as one single ArrayBuffer.
                    let fileBuffer = await storedResource.fetchChunkAtOffset(0);
                    if (fileBuffer) {
                        this._colibrioReaderPublication = await this.vanillaReadingSystem.loadPublicationFromArrayBuffer(fileBuffer.data, fileName, storeOnDevice, shelfItem).catch((reason) => {
                            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_LOAD_FAILED', {errorMessage: reason}));
                        });

                        if (!this._colibrioReaderPublication) {
                            throw new DOMException('VanillaReader.loadPublicationFromStore(), Failed to load publication');
                        }

                        await this._onAfterPublicationLoaded(fileName, fileSourceUri, this._colibrioReaderPublication, shelfItem, initialLocator);
                    } else {
                        console.warn('VanillaReader.loadPublicationFromStore(): Unable to get ArrayBuffer data for ' + storedResource.name);
                    }

                } else {
                    console.warn('VanillaReader.loadPublicationFromStore(): The fileSourceUri does not have a valid protocol (https:// or file://). ' + fileSourceUri);
                }

            } else {
                console.warn('VanillaReader.loadPublicationFromStore(): Unable to get stored publication with file name ' + fileName);
            }

        }
    }

    /**
     *
     * This method takes in a File object and tries to load a publication from it. "Under the hood" it uses the
     * Colibrio `ReadingSystemEngine`, so have a peek at the `VanillaReaderReadingSystem.loadPublicationFromFile()`
     *
     * @param file - A File object to the publication resource.
     * @param shelfItem - Shelf item is what actual represents the "used" book. It manages all the books state such as notes, bookmarks, reading positions etc.
     * @param initialLocator - Optional. A publication location to navigate to after the publication has been loaded.
     * @param storeOnDevice - Optional. Should the loaded publication data be cached on the device for offline access?.
     * @return void
     * */
    async loadPublicationFromFile(
        file: File,
        shelfItem: VanillaReaderShelfItem,
        initialLocator?: string | undefined,
        storeOnDevice: boolean = false,
    ): Promise<void> {

        if (!shelfItem) {
            throw new DOMException(`VanillaReader.loadPublicationFromFile(), No shelf item provider for ${file.name}`);
        }

        this._colibrioReaderPublication = await this.vanillaReadingSystem.loadPublicationFromFile(file, storeOnDevice, shelfItem).catch((reason) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_LOAD_FAILED', {errorMessage: reason}));
        });

        if (!this._colibrioReaderPublication) {
            throw new DOMException('VanillaReader.loadPublicationFromFile(), Failed to load publication');
        }

        // Since files loaded from disc do not have a file path, with need to create a value for the
        // file `fileSourceUri` argument. This should perhaps be done in the `VanillaReaderReadingSystem`
        // class instead.
        let fileUri = 'file://' + file.name;

        await this._onAfterPublicationLoaded(file.name, fileUri, this._colibrioReaderPublication, shelfItem, initialLocator);
    }


    getSelectorFromFragmentSelector(selectors: string[]): ILocator | null {
        if (this.vanillaPublication) {
            let selector: ISimpleLocatorData = {
                sourceUrl: this.vanillaPublication.colibrioReaderPublication.getDefaultLocatorUrl(),
                selectors: selectors
            };
            const selectorFactory = new SelectorFactory([
                new EpubCfiFragmentSelectorParser(),
                new PdfFragmentSelectorParser(),
            ]);
            return new LocatorFactory(selectorFactory).parse(selector).locator;
        }
        return null;
    }

    /**
     *
     * Navigates to the position described in the locator.
     *
     * @param locator - The locator to navigate to as an `ILocator` or as a string
     * @param options - The `IReaderViewGotoOptions.setReadingPositionToVisibleRangeStart` option "snaps" the reading
     * position to the start of the visible content if set to `true`. Default is `false` meaning that the reading position
     * will be at the precise position described in the locator.
     *
     * */
    async goTo(locator: ISimpleLocatorData | ILocator | string, options?: IReaderViewGotoOptions): Promise<void> {
        if (this.vanillaPublication) {
            if (this.vanillaReaderView) {
                let useLocator: string | ISimpleLocatorData | ILocator | null = locator;
                try {
                    await this.vanillaReaderView.goTo(locator, options);
                    useLocator = locator;
                } catch (_err){
                    console.warn("Unable to get ILocator as specified. Attempting to get ILocator from Fragment Selector");
                    if (typeof locator === 'string'){
                        useLocator = this.getSelectorFromFragmentSelector([locator]);
                        console.log("Locator is string. Using locator: ", useLocator);
                        if (useLocator){
                            await this.vanillaReaderView.goTo(useLocator, options);
                        }
                    } else {
                        console.log("Locator is type: " + typeof locator);
                    }
                }
            } else if (this.vanillaPublication?.isAudiobook) {
                let location = await this.vanillaPublication.getColibrioReaderPublication().fetchContentLocation(locator);
                this.vanillaAudiobookPlayer?.seekToLocator(location.getLocator());
            }
        }
    }

    async goToStart(): Promise<void> {
        if (!this.vanillaPublication?.isAudiobook && this.vanillaReaderView) {
            if (!this.vanillaReaderView.isAtStart()) {
                await this.vanillaReaderView.goToStart();
            }
        } else {
            this.vanillaAudiobookPlayer?.seekToApproximateTimeMs(0);
        }
    }

    async next(): Promise<void> {
        if (this.vanillaPublication?.isAudiobook) {
            await this.vanillaAudiobookPlayer?.seekToNextSegment();
        } else if (this.vanillaReaderView && this.vanillaReaderView.canPerformNext()) {
            this.vanillaReaderView.next().catch(console.error);
        }
    }

    async previous(): Promise<void> {
        if (this.vanillaPublication?.isAudiobook) {
            await this.vanillaAudiobookPlayer?.seekToPreviousSegment();
        } else if (this.vanillaReaderView && this.vanillaReaderView.canPerformPrevious()) {
            this.vanillaReaderView.previous().catch(console.error);
        }
    }

    /**
     *
     * This method navigates to a location in the book based on a given position off the readingProgressionTimeline.
     * The behavior differs a bit depending on the book format. If it's an EPUB or PDF we first need to resolve the
     * timeline position to a `IContentLocation` that we can then use as a parameter when calling `goTo`.
     * If it is an Audiobook things are much simpler. Since the timeline represents the duration of the audiobook in
     * milliseconds, we can just seek directly to that position in the book.
     *
     * */
    async gotoTimelinePosition(position: number): Promise<void> {
        if (this.vanillaReadingProgressionTimeLine) {
            if (!this.vanillaPublication?.isAudiobook) {
                let contentLocation = await this.vanillaReadingProgressionTimeLine.fetchContentLocation(position);
                if (contentLocation) {
                    await this.goTo(contentLocation.getLocator(), {setReadingPositionToVisibleRangeStart: true});
                }
            } else {
                this.vanillaAudiobookPlayer?.seekToApproximateTimeMs(position);
            }
        }
    }

    /**
     *
     * This method gets the timeline position as a number. If the loaded `IReaderPublication` is an Audiobook the number
     * represents the progression in ms. If the `IReaderPublication` is a reflowable EPUB the returned number represents
     * the index of the word at the current position. For PDF and Fixed Layout EPUBs the number represents the current
     * page index.
     *
     * */
    async getTimelinePosition(): Promise<number | undefined> {

        if (this.vanillaReadingProgressionTimeLine) {

            if (!this.vanillaPublication?.isAudiobook && this.vanillaReaderView) {
                let readingPosition = this.vanillaReaderView.getReadingPosition()?.collapseToStart().getLocator();
                if (readingPosition) {
                    return this.vanillaReadingProgressionTimeLine.fetchTimelinePosition(readingPosition);
                }
            } else {
                return this.vanillaAudiobookPlayer?.getApproximateElapsedTimeMs();
            }
        }

        return undefined;
    }

    /**
     *
     * This method searches the entire publication, or a subset of documents, for the provided search string.
     * In order to be able to perform a search, the search index must first be built. This is done adhoc in the
     * `VanillaReaderPublication.search()` method.
     *
     * */
    async searchPublication(
        searchString: string,
        resultSetMaxSize: number = 50,
        documentIndexes?: number[],
        highlightResults: boolean = true,
    ): Promise<IVanillaReaderPublicationSearchResultItem[]> {
        if (!this.vanillaPublication) {
            return [];
        }
        if (!highlightResults) {
            return this.vanillaPublicationSearch?.search(searchString, resultSetMaxSize, 50, documentIndexes) || [];
        } else {
            return this.vanillaPublicationSearch?.searchAndHighlight(searchString, resultSetMaxSize, 50, documentIndexes) || [];
        }
    }

    clearPublicationSearchLayer(): void {
        this.vanillaPublicationSearch?.clearSearch();
        const event = VanillaReaderEventBus.createEvent('PUBLICATION_SEARCH_CLEARED', {});
        VanillaReaderEventBus.dispatchEvent(event);
    }

    /**
     * This method is a quirkfix to let Screen Reader users (primarily NVDA and JAWS) highlight text based on the contents
     * of their clipboard.
     *
     * The reason that we need this is because of the Screen Readers inability to scroll the document as it moves through
     * the document contents. This is a problem for us since we use the visible range as a means to know where the user
     * is reading, so without a change in the visible range we can't can add a correct highlight or bookmark.
     *
     * The way that the Screen Reader user will use this feature is as follows:
     *
     * 1. The user selects and copies the text that represents the location they want highlight or bookmark.
     * 2. The user uses some method to trigger a UI call to this method, sending along the text that is currently
     * in the user's clipboard as well as the document index in the publication spine.
     * 3. This method uses the publication search to grab the first occurrence of the string in the specified document.
     * 4. The result is communicated back to the user using the `HIGHLIGHT_ADDED` / `BOOKMARK_ADDED` or
     * `HIGHLIGHT_TEXTLOCATION_FAILED` / `BOOKMARK_TEXTLOCATION_FAILED` events.
     *
     * */

    async bookmarkTextLocation(
        textToBookmark: string,
        documentIndex: number,
    ): Promise<void> {
        this.searchPublication(textToBookmark, 1, [documentIndex], false).then(async (resultSet) => {
            if (resultSet.length > 0) {
                return this.vanillaPublication?.addBookmark(resultSet[0].locator).then(bookmarkData => {
                    if (!bookmarkData) {
                        const event = VanillaReaderEventBus.createEvent('BOOKMARK_TEXTLOCATION_FAILED', {
                            text: textToBookmark,
                            documentIndex,
                        });
                        VanillaReaderEventBus.dispatchEvent(event);
                    }
                });
            } else {
                const event = VanillaReaderEventBus.createEvent('BOOKMARK_TEXTLOCATION_FAILED', {
                    text: textToBookmark,
                    documentIndex,
                });
                VanillaReaderEventBus.dispatchEvent(event);

            }
        }).catch(console.error);
        return;
    }

    // Bookmarks the `ContentLocation` returned by the `ReaderView`.
    async bookmarkReadingPosition(): Promise<IVanillaReaderBookmarkData | undefined> {
        let readingPositionLocator = undefined;

        if (this.vanillaPublication?.isAudiobook) {
            let timelinePosition = this.vanillaAudiobookPlayer?.getApproximateElapsedTimeMs();
            if (timelinePosition) {
                readingPositionLocator = (await this.vanillaAudiobookPlayer?.getTimeline().fetchContentLocation(timelinePosition))?.getLocator();
            }
        } else {
            readingPositionLocator = this.vanillaReaderView?.getReadingPosition()?.getLocator();
        }

        if (!readingPositionLocator) {
            console.warn(`VanillaReader.bookmarkReadingPosition(): Unable to get reading position from VanillaReaderView.`);
            return;
        }
        return this.addBookmark(readingPositionLocator);
    }

    async addHighlight(highlightData: IVanillaReaderHighlightData): Promise<IVanillaReaderHighlightData | void> {
        return this.vanillaPublication?.addHighlight(highlightData);
    }

    /**
     * This method is a quirkfix to let Screen Reader users (primarily NVDA and JAWS) highlight text based on the contents
     * of their clipboard.
     *
     * The reason that we need this is because of the Screen Readers inability to scroll the document as it moves through
     * the document contents. This is a problem for us since we use the visible range as a means to know where the user
     * is reading, so without a change in the visible range we can't can add a correct highlight or bookmark.
     *
     * The way that the Screen Reader user will use this feature is as follows:
     *
     * 1. The user selects and copies the text that represents the location they want highlight or bookmark.
     * 2. The user uses some method to trigger a UI call to this method, sending along the text that is currently
     * in the user's clipboard as well as the document index in the publication spine.
     * 3. This method uses the publication search to grab the first occurrence of the string in the specified document.
     * 4. The result is communicated back to the user using the `HIGHLIGHT_ADDED` / `BOOKMARK_ADDED` or
     * `HIGHLIGHT_TEXTLOCATION_FAILED` / `BOOKMARK_TEXTLOCATION_FAILED` events.
     *
     * */
    async highlightTextLocation(
        textToHighlight: string,
        documentIndex: number,
        sourceHighlightData: IVanillaReaderHighlightData,
    ): Promise<void> {
        await this.searchPublication(textToHighlight, 1, [documentIndex], false).then((resultSet) => {
            if (!this.vanillaPublication) {
                console.warn('VanillaReader.highlightTextSelection(), no publication loaded.');
                return;
            }
            if (resultSet.length > 0) {
                if (resultSet[0].locator) {
                    sourceHighlightData.locator = resultSet[0].locator;
                    this.addHighlight(sourceHighlightData).catch(console.warn);
                }
            } else {
                const event = VanillaReaderEventBus.createEvent('HIGHLIGHT_TEXTLOCATION_FAILED', {
                    text: textToHighlight,
                    documentIndex,
                    highlightData: sourceHighlightData,
                });
                VanillaReaderEventBus.dispatchEvent(event);
            }
        }).catch(console.error);
    }

    /**
     *
     * This method helps a Screen Reader focus on the current reading position by calling `HTMLElement.focus()` as close
     * to the current reading position as possible.
     *
     * */
    async focusOnReadingPosition(): Promise<void> {
        if (this.vanillaReaderView) {
            return this.vanillaReaderView.focusOnReadingPosition({
                focusOnPageContainer: false,
                focusOnPageBodyElement: false,
                focusNearContentLocation: true,
            }).then(()=>{
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('FOCUS_ON_READINGPOSITION_DONE', {}));
            }).catch((reason)=>{
                console.error(reason);
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('FOCUS_ON_READINGPOSITION_FAILED', {reason}));
            });
        }
    }

    ignorePublicationAspectRatio(ignoreAspectRatio: boolean = false): void {
        this.vanillaReaderView?.changeRendererAspectRatio(ignoreAspectRatio);
    }

    async fetchPublicationLandmarks(landmarkTypesToInclude?: VanillaReaderPublicationLandmarkType[]): Promise<IVanillaReaderPublicationLandmarkCollection | undefined> {
        let headings, footnotes, figures, tables, lists;

        if (!this.vanillaPublication?.progressionTimeline) {
            console.warn('VanillaReader.fetchPublicationLandmarks, reading progression timeline not initialized.');
            return;
        }

        await this.vanillaPublication?.fetchLandmarkCollection(this.vanillaPublication.progressionTimeline, landmarkTypesToInclude).then((landmarkCollection) => {
            headings = landmarkCollection?.getHeadings();
            footnotes = landmarkCollection?.getFootnotes();
            figures = landmarkCollection?.getFigures();
            tables = landmarkCollection?.getTables();
            lists = landmarkCollection?.getLists();
        });
        return {
            headings,
            footnotes,
            figures,
            tables,
            lists,
        };

    }

    async fetchDocumentLandmarks(documentIndex: number, landmarkTypesToInclude?: VanillaReaderPublicationLandmarkType[]): Promise<IVanillaReaderDocumentLandmark> {
        let landmarkCollection = await this.vanillaPublication?.fetchLandmarkCollectionForDocument(documentIndex, this.vanillaReadingProgressionTimeLine, landmarkTypesToInclude);
        let headings = landmarkCollection?.getHeadings();
        let footnotes = landmarkCollection?.getFootnotes();
        let figures = landmarkCollection?.getFigures();
        let tables = landmarkCollection?.getTables();
        let lists = landmarkCollection?.getLists();

        return {
            documentIndex: documentIndex,
            headings,
            footnotes,
            figures,
            tables,
            lists,
        };

    }

    navigate(navigationAction: IVanillaReaderNavigationAction): void {

        switch (navigationAction.navigationType) {
            case VanillaReaderNavigationType.NEXT:
                this.next().catch(console.warn);
                break;
            case VanillaReaderNavigationType.PREVIOUS:
                this.previous().catch(console.warn);
                break;
            case VanillaReaderNavigationType.TIMELINEPOSITION:
                if (navigationAction.position !== undefined) {
                    this.gotoTimelinePosition(navigationAction.position).catch(console.error);
                } else {
                    console.warn('VanillaReader.event_navigationIntent()', 'event.detail.position is undefined');
                }
                break;
            case VanillaReaderNavigationType.GOTO:
                if (navigationAction.locator) {
                    this.goTo(navigationAction.locator).catch(console.error);
                } else {
                    console.warn('VanillaReader.event_navigationIntent()', 'event.detail.location is undefined');
                }
                break;
            case VanillaReaderNavigationType.GOTOSTART:
                this.goToStart().catch(console.error);
                break;
        }

        if (this.shouldFocusOnReadingPosition && navigationAction.hasOwnProperty('focusNearContentLocation') && navigationAction.focusNearContentLocation) {
            VanillaReaderEventBus.addEventListener('READING_POSITION_UPDATED', () => {
                this.focusOnReadingPosition().catch(console.warn);
            }, true);
        }
    }

    addBookmark(bookmarkLocator: string | ILocator): Promise<IVanillaReaderBookmarkData | undefined> | undefined {
        return this.vanillaPublication?.addBookmark(bookmarkLocator);
    }

    deleteBookmark(bookmarkLocator: string): void {
        if (bookmarkLocator) {
            this.vanillaPublication?.deleteBookmark(bookmarkLocator).catch(console.warn);
        }
    }

    updateHighlight(highlightData: IVanillaReaderHighlightData): void {
        if (this.vanillaPublication) {
            this.vanillaPublication.updateHighlight(highlightData).catch(console.warn);
        }
    }

    deleteHighlight(highlightLocator: string): void {
        this.vanillaPublication?.deleteHighlight(highlightLocator).catch(console.warn);
    }

    setPublicationOptions(styleOptions: IEpubReaderPublicationOptions | IPdfReaderPublicationOptions | IWpAudiobookReaderPublicationOptions): void {
        if (this.vanillaPublication) {
            this.vanillaPublication.setPublicationOptions(styleOptions);
        }
    }

    setPublicationStyleOptions(styleOptions: IVanillaReaderPublicationStyleOptions): void {
        if (this.vanillaReaderView) {
            this.vanillaPublication?.setPublicationStyleOptions(styleOptions, this.vanillaReaderView);
        }
    }

    revokeMediaObjectUrl(mediaUrl?: string): void {
        if (mediaUrl) {
            this.vanillaPublication?.revokeResourceBlobUrl(mediaUrl);
        } else {
            // The media url was undefined, let's just revoke all urls to be on the safe side. Don't want to leak memory.
            this.vanillaPublication?.revokeAllResourceBlobUrls();
        }
    }

    async fetchContentPositionDataForHref(href: string, baseDocumentLocator: string): Promise<IVanillaReaderContentPositionData | undefined> {
        let baseLocation = await this.vanillaPublication?.colibrioReaderPublication.fetchContentLocation(baseDocumentLocator);

        if (!baseLocation) {
            console.warn(`VanillaReader.fetchContentPositionDataForHref(), Unable to resolve ContentLocation from locator "${baseDocumentLocator}"`);
            return;
        }

        let baseUrl = baseLocation.getReaderDocuments()[0].getSourceContentDocument().getContentUrl();
        return this.vanillaReaderView?.fetchContentPositionDataFromHref(href, baseUrl);
    }

    deletePublicationCacheData(publicationId: string): void {
        this.vanillaPublication?.deleteStoredResources().then(() => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_STORED_RESOURCES_DELETED', {publicationId}));
        });
    }

    toggleReaderViewPageMarkerVisibility(visible: boolean = true): void {
        if (!this.vanillaReaderView?.pageMarkerLayer) {
            console.warn('VanillaReader._uiEvent_viewPageNumbersVisibilityToggleIntent(), No VanillaReaderAnnotationLayerPageRanges has been created yet.');
            return;
        }

        this.vanillaReaderView.togglePageMarkerLayer(visible);

        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_PAGE_MARKERS_VISIBILITY_CHANGED', {visible: this.vanillaReaderView.pageMarkerLayer.isVisible}));

    }

    useReducedMotion(useReducedMotion: boolean): void {
        if (this.vanillaReaderView) {
            this.vanillaReaderView.useReducedMotion = useReducedMotion;
        }
    }

    scrollOverflowingTables(scrollOverflowingTables: boolean): void {
        if (this.vanillaReaderView) {
            this.vanillaReaderView.scrollOverflowingTables = scrollOverflowingTables;
        }
    }

    changeRenderer(rendererName: VanillaReaderRendererNames, ignoreAspectRatio: boolean): void {
        if (rendererName && Object.values(VanillaReaderRendererNames).includes(rendererName)) {
            this.vanillaReaderView?.changeRenderer(rendererName, ignoreAspectRatio);
        }
    }

    refreshReaderView(force: boolean = false): void {
        this.vanillaReaderView?.refresh(force);
    }

    clearContentSelection(): void {
        this.vanillaReaderView?.clearContentSelection();
    }

    async toggleMediaPlayer(activate: boolean, options: IVanillaReaderSyncMediaOptions): Promise<void> {
        if (this.vanillaReaderView) {

            let completeOptions = Object.assign(this._initialOptions.syncMediaOptions ||{}, options);

            if (activate) {
                let player = this.vanillaMediaPlayer;
                if (!player) {
                    player = await this._createSyncMediaPlayer(this.vanillaReaderView, completeOptions);
                }
                player.toggle(activate);

            } else {
                if (this.vanillaMediaPlayer) {
                    this.vanillaMediaPlayer.pause();
                    this.vanillaMediaPlayer.toggle(false);
                }
            }
        } else {
            console.warn('VanillaReader._uiEvent_mediaPlayerToggleIntent(): No _colibrioReaderView instance found. Is this an audiobook?');
        }
    }

    seekMediaPlaybackNext(): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.seekToNext();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.seekToNextSegment().catch(console.warn);
        }
    }

    seekMediaPlaybackPrevious(): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.seekToPrevious();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.seekToPreviousSegment().catch(console.warn);
        }
    }

    seekMediaPlaybackForward(): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.seekForward();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.seekForward();
        }
    }

    seekMediaPlaybackBackward(): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.seekBackward();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.seekBackward();
        }
    }

    seekMediaToPlaybackRangeStart(): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.seekToPlaybackRangeStart();
        }
    }

    seekMediaToLocator(locator: ILocator | ILocatorData): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.seekToLocator(locator).catch(console.warn);
        }
    }

    seekMediaToLocatorWithinVisibleRange(locator: ILocator | ILocatorData): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.seekToLocatorWithinVisibleRange(locator).catch(console.warn);
        }
    }

    async startMediaPlayback(locator?: ILocatorData): Promise<void> {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            if (locator) {
                await this.vanillaMediaPlayer.seekToLocator(locator);
            }
            this.vanillaMediaPlayer.play();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.play().catch(console.warn);
        }
    }

    pauseMediaPlayback(): void {
        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.pause();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.pause().catch(console.warn);
        }
    }

    setMediaPlaybackRate(rate: number): void {
        if (!this.vanillaPublication) {
            return;
        }

        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.setPlaybackRate(rate);
            this.vanillaPublication.ttsOptions = this.vanillaMediaPlayer.getTtsPlaybackOptions();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.setPlaybackRate(rate);
        }
    }

    setMediaPlaybackVolume(volume: number): void {
        if (!this.vanillaPublication) {
            return;
        }

        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {
            this.vanillaMediaPlayer.setVolume(volume);
            this.vanillaPublication.ttsOptions = this.vanillaMediaPlayer.getTtsPlaybackOptions();
        } else if (this.vanillaAudiobookPlayer) {
            this.vanillaAudiobookPlayer.setVolume(volume);
        }
    }

    async setTtsVoice(voiceName: string | undefined): Promise<void> {
        if (!this.vanillaPublication) {
            return;
        }

        if (this.vanillaMediaPlayer && this.vanillaMediaPlayer.playerIsAttachedToView) {

            await this.vanillaMediaPlayer.setTtsVoiceByName(voiceName).catch(console.warn);

            // Persist the options change in the VanillaPublication instance so that it will be serialized properly.
            this.vanillaPublication.ttsOptions = this.vanillaMediaPlayer.getTtsPlaybackOptions();

            // Back up the media player to the start of the phrase so that it repeats it using the new voice.
            let readingPosition = this.vanillaReaderView?.getReadingPosition();
            if (readingPosition) {
                this.vanillaMediaPlayer.seekToLocator(readingPosition.getLocator()).catch(console.warn);
            }

            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_VOICE_CHANGED', {voiceName}));
        }
    }

    async fetchTtsVoiceCollection(): Promise<ISpeechSynthesisVoiceCollection | undefined> {
        let voicesCollection: ISpeechSynthesisVoiceCollection | undefined;

        if(this._initialOptions.syncMediaOptions?.webSpeechTtsSynthesizer) {
            voicesCollection = await this._initialOptions.syncMediaOptions?.webSpeechTtsSynthesizer.fetchVoices();
        } else {
            voicesCollection =  await WebSpeechUtils.fetchTtsVoices();
        }

        return voicesCollection;
    }

    activatePanZoomMode(): void {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_TRANSFORM_PAN_MODE_ACTIVATED', {}));
    }

    deactivatePanZoomMode(): void {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_TRANSFORM_PAN_MODE_DEACTIVATED', {}));
    }

    async zoomToClientRect(zoomRect: DOMRect, animate: boolean = true): Promise<IVanillaReaderTransformData | null> {
        return this.vanillaReaderView?.zoomToClientRect(zoomRect, animate) || null;
    }

    zoomToPublicationViewportClientRect(zoomRect: DOMRect, unit: LengthUnit = LengthUnit.PERCENT, transformOptions: ITransformRectZoomOptions): void {
        this.vanillaReaderView?.zoomToPublicationViewportRect(zoomRect, unit, transformOptions);
    }

    resetZoom(): void {
        this.vanillaReaderView?.resetViewTransformation();
    }

    async zoomToLevel(zoomLevel: number, animate: boolean = true): Promise<ITransformData | null> {
        return this.vanillaReaderView?.zoomToLevel(zoomLevel, animate) || null;
    }

    /*
    *
    * PRIVATE METHODS
    *
    * */

    /*
    *
    * You can't have an event driven architecture without events right? Let's add event listeners.
    * Almost all events are intents sent from the `VanillaReaderUI` to the `VanillaReader` as it wants to perform
    * actions, such as navigations etc.
    * */
    private _setUpEventBusEventHandlers(): void {
        VanillaReaderEventBus.addEventListener('APP_ONLINE_STATE_CHANGED', this._event_appOnlineStateChanged);
        VanillaReaderEventBus.addEventListener('TIMELINE_READY', this._event_timelineReady);
        VanillaReaderEventBus.addEventListener('MEDIA_OBJECT_CLICKED', this._event_mediaObjectClicked);
        VanillaReaderEventBus.addEventListener('BOOKMARK_ADDED', this._event_bookmarkAdded);
        VanillaReaderEventBus.addEventListener('BOOKMARK_DELETED', this._event_bookmarkDeleted);
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_ADDED', this._event_highlightAdded);
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_DELETED', this._event_highlightDeleted);
        VanillaReaderEventBus.addEventListener('HIGHLIGHT_UPDATED', this._event_highlightUpdated);
        //VanillaReaderEventBus.addEventListener('');
    }

    /*
    *
    * Whereas events are used for module to module communication, callbacks are used  within the `VanillaReader` module to
    * act on "child component" events.
    *
    * */
    private _setUpComponentCallbacks(): void {

        /**
         * READING SYSTEM CALLBACKS
         * */

        this.vanillaReadingSystem.onPublicationRendered(() => {
            const event = VanillaReaderEventBus.createEvent('PUBLICATION_RENDERED', {});
            VanillaReaderEventBus.dispatchEvent(event);
        });

        this.vanillaReadingSystem.onReadingPositionChanged((ev: IReaderViewEngineEvent) => {
            this._event_readingPositionChanged(ev).catch(console.warn);
        });

        this.vanillaReadingSystem.onNavigationStarted((ev: INavigationStartedEngineEvent) => {
            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('NAVIGATION_STARTED', {srcEventData: ev.toJSON()}),
            );
        });

        this.vanillaReadingSystem.onNavigationEnded((ev: INavigationEndedEngineEvent) => {
            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('NAVIGATION_COMPLETED', {
                    srcEventData: ev.toJSON(),
                }),
            );
        });

        this.vanillaReadingSystem.onNavigationIntent(async (ev: INavigationIntentEngineEvent) => {
            ev.preventDefault();
            // Checkout the `ui_event_navigationIntent` for the other navigation code when navigation intents are sent
            // from the UI.

            if (!ev.internalNavigation) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('NAVIGATION_EXTERNAL_INTENT', {
                    targetUrl: ev.locator.toString(),
                }));
            } else {
                let targetContentPositionData: IVanillaReaderContentPositionData | undefined;

                if(this._colibrioReaderPublication && ev.relatedEvent?.target?.contentLocation && VanillaReaderUtils.isEpubReaderPublication(this._colibrioReaderPublication)) {
                    targetContentPositionData = await this.vanillaReaderView?.fetchContentPositionData(ev.relatedEvent?.target?.contentLocation);
                }
                // Since the UI logic may want to act on this event, if it leads to a footnote for example, we prevent it.
                // The event data contains the information needed for the UI to perform the navigation manually.
                VanillaReaderEventBus.dispatchEvent(
                    VanillaReaderEventBus.createEvent('NAVIGATION_INTERNAL_INTENT', {srcEvent: ev, targetContentPositionData}),
                );
            }
        });

        this.vanillaReadingSystem.onClick(async (ev: IMouseEngineEvent) => {
            let contentPositionData: IVanillaReaderContentPositionData | undefined = ev.target?.contentLocation ?
                await this.vanillaReaderView?.fetchContentPositionData(ev.target.contentLocation) :
                undefined;

            if (ev.mediaResource) {

                VanillaReaderEventBus.dispatchEvent(
                    VanillaReaderEventBus.createEvent('MEDIA_OBJECT_CLICKED', {mediaResourceData: ev.toJSON().mediaResource}),
                );
                ev.preventDefault();
            }

            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('CLICK', {srcEventData: ev.toJSON(), contentPositionData}));

        });

        this.vanillaReadingSystem.onPointerUp(async (ev: IPointerEngineEvent) => {
            let contentPositionData: IVanillaReaderContentPositionData | undefined = ev.target?.contentLocation ?
                await this.vanillaReaderView?.fetchContentPositionData(ev.target.contentLocation) :
                undefined;

            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('POINTER_UP', {srcEventData: ev.toJSON(), contentPositionData}),
            );
        });

        this.vanillaReadingSystem.onPointerDown(async (ev: IPointerEngineEvent) => {
            let contentPositionData: IVanillaReaderContentPositionData | undefined = ev.target?.contentLocation ? await this.vanillaReaderView?.fetchContentPositionData(ev.target.contentLocation) : undefined;

            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('POINTER_DOWN', {srcEventData: ev.toJSON(), contentPositionData})
            );
        });

        this.vanillaReadingSystem.onPointerMove((ev: IPointerEngineEvent) => {
            //let contentPositionData: IVanillaReaderContentPositionData | undefined  = ev.target?.contentLocation ? await this.vanillaReaderView?.fetchContentPositionData(ev.target.contentLocation) : undefined;

            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent('POINTER_MOVE', {srcEventData: ev.toJSON(), contentPositionData: undefined}),
            );
        });

        this.vanillaReadingSystem.onVisibleRangeChanged(() => {
            this._event_visibleRangeDataChanged().catch(console.warn);
        });

        this.vanillaReadingSystem.onSelectionChanged((ev: ISelectionChangedEngineEvent) => {
            this._event_textSelected(ev);
        });

        this.vanillaReadingSystem.onKeyDown((ev: IKeyboardEngineEvent) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('KEY_DOWN', {srcEventData: ev.toJSON()}));
        });

        this.vanillaReadingSystem.onKeyUp((ev: IKeyboardEngineEvent) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('KEY_UP', {srcEventData: ev.toJSON()}));
        });

        this.vanillaReadingSystem.onPublicationDownloadProgress(((progress, url) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent(
                'PUBLICATION_DOWNLOAD_PROGRESS_UPDATED',
                {
                    progress,
                    publicationId: url,

                },
            ));
        }));

        /*
        *
        * STORAGE CALLBACKS
        *
        * */

        // These are set up when the SyncMediaPlayer is created. Check out _createSyncMediaPlayer().

    }

    /*
    * _onAfterPublicationLoaded is called after we have a ReaderPublication. This is when we can do all fun stuff such as,
    *
    * - build the search index
    * - build the navigation tree
    * - etc
    *
    * And once all that is done, we can dispatch the `IVanillaReaderAppEvent_PUBLICATION_LOADED` event to everyone that
    * are listening.
    *
    * It has some less optimal aspects still as it has some reliance on the order in which some methods are
    * called. Refactoring is on the todo 🙂
    *
    * */
    private async _onAfterPublicationLoaded(
        fileName: string,
        fileSourceUri: string,
        colibrioReaderPublication: IReaderPublication,
        shelfItem: VanillaReaderShelfItem,
        initialLocator?: string | undefined,
    ): Promise<void> {
        this.vanillaPublication = await VanillaReaderPublication.create(fileName, fileSourceUri, colibrioReaderPublication, shelfItem);

        // TODO: Move to VanillaReaderPublication.ts.
        let initialPublicationCacheDataState = await shelfItem?.fetchCacheData();

        if (this.vanillaPublication.isAudiobook) {

            await this._createAudiobookView(this.vanillaPublication);

            this.vanillaAudiobookPlayer = new VanillaReaderAudiobookPlayer(this.vanillaPublication);

            this.vanillaAudiobookPlayer.onMediaPlayerCreated(async () => {

                this.vanillaReadingProgressionTimeLine = await this._createReadingProgressionTimeline(this.vanillaPublication!);

                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_CREATED', {}));
            });

            this.vanillaAudiobookPlayer.onMediaPlayerCreateProgressUpdated((progress: number) => {
                VanillaReaderEventBus.dispatchEvent(
                    VanillaReaderEventBus.createEvent('MEDIAPLAYER_CREATE_PROGRESS_UPDATED', {progress},
                    ));
            });

            await this.vanillaAudiobookPlayer.createSyncMediaTimeLine();

            this.vanillaAudiobookPlayer.onMediaPlayerPlaybackStateChanged((state: IVanillaSyncMediaPlaybackStateData) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_PLAYBACK_STATE_CHANGED', {state}));
            });

            this.vanillaAudiobookPlayer.onMediaPlayerPlaybackPositionChanged(async (state: IVanillaSyncMediaPlaybackStateData) => {

                let timelinePosition = state.position;
                let contentLocation = await this.vanillaAudiobookPlayer?.getTimeline().fetchContentLocation(timelinePosition);

                if(contentLocation) {

                    let locator = contentLocation.getLocator().toString();
                    const readingPositionData: IVanillaReaderContentPositionData = {
                        publicationId: this.vanillaPublication?.id,
                        locator,
                        timestamp: Date.now(),
                        navItemData: state.navigationItems,
                    }

                    if (readingPositionData && this.vanillaPublication) {
                        this.vanillaPublication.setCurrentReadingPositionData(readingPositionData);
                        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('READING_POSITION_UPDATED', {readingPositionData}));
                    }
                }

                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_PLAYBACK_POSITION_CHANGED', {state}));
            });

            this.vanillaAudiobookPlayer.onMediaPlayerTrackChanged((state: IVanillaSyncMediaPlaybackStateData) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_SEEK_COMPLETED', {state}));
            });

        } else {

            /*
            *
            * EPUB and PDF specific setup
            *
            * */

            this.vanillaReaderView = new VanillaReaderView(this.vanillaReadingSystem, this.vanillaPublication, this.readerViewElement, this.vanillaOptionsDataStore, this._initialOptions?.customReaderViewContentOnLoading, this._initialOptions?.customReaderViewContentOnLoadError);

            this.vanillaReaderView.onActiveRendererChanged((ev) => {
                const rendererName = ev.newActiveRenderer?.getName();
                const newRendererName: VanillaReaderRendererNames | undefined =
                    (rendererName && Object.values(VanillaReaderRendererNames).some(v => v === rendererName)) ?
                        rendererName as VanillaReaderRendererNames : undefined;

                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('RENDERER_CHANGED', {
                    newActiveRendererName: newRendererName,
                    newActiveRendererOptions: ev.newActiveRenderer?.getOptions(),
                }));
            });

            this.vanillaReaderView.onRendererTransitionStarted((ev) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_TRANSITION_STARTED', {srcEventData: ev.toJSON()}));
            });

            this.vanillaReaderView.onRendererTransitionEnded((ev) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_TRANSITION_ENDED', {srcEventData: ev.toJSON()}));
            });

            this.vanillaReaderView.onReducedMotionValueChanged((preference: boolean) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('REDUCED_MOTION_OPTION_CHANGED', {useReducedMotion: preference}));
            });

            this.vanillaReaderView.onIgnoreAspectRatioValueChanged((ignore) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('RENDERER_ASPECT_RATIO_CHANGED', {ignoreAspectRatio: ignore}));
            });

            this.vanillaReaderView.onSwipeNavigationGestureProgress((eventData: ISwipeNavigationGestureProgressEngineEventData) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('VIEW_SWIPE_NAVIGATION_PROGRESS_UPDATED', {srcEventData: eventData}));
            });

            // Now we need to do some asynchronous set up, so we call the async _setup() method.
            await this.vanillaReaderView.setup().catch(console.warn);

            var that = this;
            this._createReadingProgressionTimeline(this.vanillaPublication, initialPublicationCacheDataState?.timelineData).then((timeline)=>{
                that.vanillaReadingProgressionTimeLine = timeline;
            });

            this.vanillaPublicationSearch = new VanillaReaderPublicationSearch(this.vanillaPublication.colibrioReaderPublication, this.vanillaReaderView.colibrioReaderView);
            
            this.vanillaPublicationSearch?.onSearchIndexCreateProgressUpdate((progress: number) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_SEARCH_INDEX_BUILD_PROGRESS', {progress}));
            });

            this.vanillaPublicationSearch?.onSearchIndexCreated(() => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_SEARCH_INDEX_READY', {}));
            });

        }

        // We want to preserve all style options *except* for palette which the user will expect to mirror the app theme.
        if (this._initialOptions?.viewOptions?.colibrioReaderViewOptions?.publicationStyleOptions?.palette) {
            this.vanillaReaderView?.setVanillaReaderViewOptions({
                colibrioReaderViewOptions: {
                    publicationStyleOptions: {
                        palette: this._initialOptions?.viewOptions?.colibrioReaderViewOptions?.publicationStyleOptions?.palette,
                    },
                },
            });
        }

        /**
         * If the book has been opened by the user before it will probably have some state that should be restored from
         * the previous session, such as style publicationStyleOptions, bookmarks and highlights.
         **/

        await this._restoreBookmarks().catch(console.warn);
        await this._restoreHighlights().catch(console.warn);

        
        this.goToStart().catch(console.error);
        
        // The publication is loaded, and we are ready to tell the UI that it can do its thing.
        // We start with serializing the initial publication state so that we can send it back to the UI.
        let vanillaPublicationData = await this.vanillaPublication.serialize();
        let publicationOptionsData = await this.vanillaPublication.fetchPublicationOptionsData();

        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_LOADED', {
            initialPosition: initialLocator,
            publicationData: vanillaPublicationData,
            publicationOptionsData
        }));

        this.isLoaded = true;
    }

    /*
    *
    * _createAudiobookView renders a simple view that contains the audiobook cover image. It also adds pointer event
    * listeners to the cover image containers.
    *
    * For EPUB and PDF publications, please check out the _createReaderView method.
    *
    * */

    private async _createAudiobookView(vanillaReaderPublication: VanillaReaderPublication): Promise<void> {
        this.vanillaAudiobookView = new VanillaReaderAudiobookReaderView(vanillaReaderPublication, this.readerViewElement);
        return this.vanillaAudiobookView.render();
    }

    /*
    *
    * The VanillaReader has a general implementation of a reading progress timeline in order to abstract away the differences
    * between Audiobooks and text based books such as EPUB and PDF (`IVanillaReaderReadingProgressionTimeline`).
    *
    * "Under the hood" Audiobooks use the Colibrio `SyncMediaTimeline`, for EPUB and PDF the `ContentPositionTimeline`
    * is used.
    *
    * This method creates and returns a new instance of the `IVanillaReaderReadingProgressionTimeline` which is then used
    * in
    *
    * */
    private async _createReadingProgressionTimeline(
        vanillaPublication: VanillaReaderPublication,
        timelineSerializedData?: string | undefined,
    ): Promise<IVanillaReaderReadingProgressionTimeline | undefined> {

        let readingProgressionTimeLine: IVanillaReaderReadingProgressionTimeline | undefined;

        if (!vanillaPublication.isAudiobook && this.vanillaReaderView) {
            /*
            * EPUB and PDF specific setup
            * */
            readingProgressionTimeLine = await VanillaReaderProgressionTimeline.create(this.vanillaReaderView, vanillaPublication, timelineSerializedData, (progress: number) => {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('TIMELINE_BUILD_PROCESS_UPDATED', {
                    progress,
                }));
            });

        } else if (this.vanillaAudiobookPlayer) {
            /*
            * Audiobook specific setup
            * */
            if (this.vanillaAudiobookPlayer.hasBeenCreated) {
                readingProgressionTimeLine = this.vanillaAudiobookPlayer.getTimeline();
            }
        }

        vanillaPublication.progressionTimeline = readingProgressionTimeLine;
        this.vanillaReadingProgressionTimeLine = readingProgressionTimeLine;

        readingProgressionTimeLine?.onTimelinePositionUpdated((position: number) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('TIMELINE_POSITION_UPDATED', {
                timelinePosition: position,
            }));
        });

        // The timeline can take a moment to build, so we need to send an event when it has finished building. Check out
        // the `_event_timelineReady` handler to follow this thread in the `VanillaReader`.
        readingProgressionTimeLine?.onTimelineReady((timelineData: IVanillaReaderReadingProgressionTimelineData) => {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('TIMELINE_READY', {timelineData}));
        });


        return readingProgressionTimeLine;
    }

    private _registerWordRangeReadToStats(wordRange: IntRange): void {
        this.vanillaReaderView?.readingStats?.addWordRange(wordRange);
    }

    /**
     *
     * This method creates an instance of a `VanillaReaderMediaPlayer`. This object will be used for playback of TTS or
     * EPUB Media Overlays.
     *
     * If you are looking for the method that creates the audiobook player it is done in the `_onAfterPublicationLoaded`.
     *
     * */
    private async _createSyncMediaPlayer(
        vanillaReaderView: VanillaReaderView,
        syncMediaOptions?: IVanillaReaderSyncMediaOptions
    ): Promise<VanillaReaderMediaPlayer> {
        this.vanillaMediaPlayer = new VanillaReaderMediaPlayer(vanillaReaderView,
            syncMediaOptions,
            this._event_onSyncMediaPlayerCreateProgress
        );

        this.vanillaMediaPlayer.onAttached(this._event_onSyncMediaPlayerAttached);
        this.vanillaMediaPlayer.onDetached(this._event_onSyncMediaPlayerDetached);
        this.vanillaMediaPlayer.onCreated(this._event_onSyncMediaPlayerCreated);
        this.vanillaMediaPlayer.onPlaybackStateChanged(this._event_onSyncMediaPlayerPlaybackStateChanged);
        this.vanillaMediaPlayer.onRangePlaybackRemoved(this._event_onSyncMediaRangePlaybackRemoved);
        this.vanillaMediaPlayer.onRangePlaybackEndReached(this._event_onSyncMediaRangePlaybackEndReached);
        this.vanillaMediaPlayer.onEscapabilityContextChanged(this._event_onSyncMediaEscapabilityContentChanged);


        await this.vanillaMediaPlayer.create().catch(console.warn);

        this.vanillaMediaPlayer.setEscapabableContentBlockTypes([
            ContainerContentBlockType.ASIDE,
            ContainerContentBlockType.TABLE,
            ContainerContentBlockType.TABLE_CELL,
            ContainerContentBlockType.TABLE_ROW,
            ContainerContentBlockType.ORDERED_LIST,
            ContainerContentBlockType.UNORDERED_LIST,
            ContainerContentBlockType.DESCRIPTION_LIST,
            ContainerContentBlockType.LIST_ITEM,
            ContainerContentBlockType.MATHML,
            ContainerContentBlockType.MATHML_TABLE,
            ContainerContentBlockType.PREFORMATTED,

        ]);


        return this.vanillaMediaPlayer;
    }

    private async _restoreBookmarks(_silent: boolean = true): Promise<void> {

        if (this.vanillaPublication?.id) {
            let bookmarks = await this.vanillaPublication.fetchBookmarks();
            this.vanillaReaderView?.renderBookmarks(bookmarks);

            // Dispatch events so that the UI can update its state.
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARKS_LOADED', {bookmarks}));
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARKS_COLLECTION_UPDATED', {bookmarks}));
        }
    }

    private async _restoreHighlights(_silent: boolean = true): Promise<void> {

        if (this.vanillaPublication && this.vanillaPublication.id) {
            let highlights = await this.vanillaPublication.fetchHighlights();
            this.vanillaReaderView?.renderHighlights(highlights);

            // Dispatch events so that the UI can update its state.
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHTS_LOADED', {highlights}));
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHTS_COLLECTION_UPDATED', {highlights}));
        }
    }

    /*
    *
    * EVENT HANDLERS
    *
    **/

    private _event_onSyncMediaPlayerAttached = () => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_ATTACHED', {}));
    };

    private _event_onSyncMediaPlayerDetached = () => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_DETACHED', {}));
    };

    private _event_onSyncMediaPlayerCreated = () => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_CREATED', {}));
    };

    private _event_onSyncMediaPlayerCreateProgress = (progress: number) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_CREATE_PROGRESS_UPDATED', {progress}));
    };

    private _event_onSyncMediaPlayerPlaybackStateChanged = (state: IVanillaSyncMediaPlaybackStateData) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_PLAYBACK_STATE_CHANGED', {state}));
    };

    private _event_onSyncMediaRangePlaybackEndReached = (state: IVanillaSyncMediaPlaybackStateData) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_PLAYBACK_RANGE_END_REACHED', {state}));
    };

    private _event_onSyncMediaEscapabilityContentChanged = (context: IVanillaReaderEscapableContentBlockContext[]) => {
        let contextData: IVanillaReaderEscapableContentBlockDataContext[] =  context.map<IVanillaReaderEscapableContentBlockDataContext>((contextItem)=>{
           return {
               blockData: contextItem.block.toSerializableData({createLocators: true}),
               blockId: contextItem.blockId,
               blockStartLocator: contextItem.blockRangeLocation.collapseToStart().getLocator().toString(this.vanillaPublication?.url),
               blockEndLocator: contextItem.blockRangeLocation.collapseToEnd().getLocator().toString(this.vanillaPublication?.url)
           }
        });
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_ESCAPABILITY_CONTEXT_CHANGED', {contextData}));
    };

    private _event_onSyncMediaRangePlaybackRemoved = (state: IVanillaSyncMediaPlaybackStateData, reason?: SyncMediaRangePlaybackRemoveReason) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIAPLAYER_PLAYBACK_RANGE_REMOVED', {state, reason}));
    };

    private _event_mediaObjectClicked = async (ev: CustomEvent<IVanillaReaderAppEvent_MEDIA_OBJECT_CLICKED>) => {

        if (this.vanillaPublication && !this.vanillaPublication.isFixedLayout) {
            let mediaResourceData: IEngineEventMediaResourceData | null = ev.detail.mediaResourceData;
            let mediaElementAttributes: IAttributeData[] | undefined;

            if (mediaResourceData && mediaResourceData.resourceUrl && mediaResourceData.mediaTypeCategory === MediaTypeCategory.IMAGE) {
                let mediaResourceBlobUrl = await this.vanillaPublication.fetchResourceBlobUrl(mediaResourceData.resourceUrl);
                this.vanillaPublication.fetchLandmarkCollection().then((landmarkCollection: VanillaReaderPublicationLandmarkCollection | undefined) => {

                    if (landmarkCollection) {
                        let mediaElementUrl = new URL(mediaResourceData!.resourceUrl!);
                        let mediaElement = landmarkCollection.getFigures()?.find((mediaElement) => {
                            if (!mediaElement || !mediaElement.resourceHrefs || mediaElement.resourceHrefs.length === 0) {
                                return false;
                            }
                            return mediaElementUrl.pathname.includes(mediaElement.resourceHrefs[0]);
                        });

                        mediaElementAttributes = mediaElement ? mediaElement.attributes : undefined;

                    }

                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('MEDIA_OBJECT_VIEW_INTENT', {
                        mediaUrl: mediaResourceBlobUrl,
                        mediaElementAttributes,
                    }));
                }).catch(console.warn);
            }
        }
    };

    private _event_timelineReady = async () => {
        if (this.vanillaPublication && this.vanillaPublication.progressionTimeline) {

            this.vanillaReaderView?.readingStats?.setPublicationLength(this.vanillaPublication.progressionTimeline.getLength());
            
            const readingPosition = this.vanillaReaderView?.getReadingPosition();
            if (readingPosition){
                this.vanillaReadingProgressionTimeLine?.updateTimelinePosition(readingPosition.getLocator());
            }

            await this.vanillaPublication?.buildNavigationTree(this.vanillaPublication.progressionTimeline).catch(console.error);
            await this.vanillaPublication?.buildPageList(this.vanillaPublication.progressionTimeline).catch(console.error);

            if (this.vanillaReaderView && this.vanillaPublication.pageListData) {
                this.vanillaReaderView.createPageMarkerLayer(this.vanillaPublication.pageListData);
            }

            // As we now have both timeline and navigation tree, we can send more complete visible range data to the UI.
            this._event_visibleRangeDataChanged().catch(console.error);
        }
    };

    private _event_readingPositionChanged = async (ev: IReaderViewEngineEvent) => {
        const readingPosition = ev.readerView.getReadingPosition();
        const readingPositionData = await this.vanillaReaderView?.getReadingPositionData();

        if (readingPosition && readingPositionData && this.vanillaPublication && this.vanillaPublication.id) {
            this.vanillaPublication.setCurrentReadingPositionData(readingPositionData);
            this.vanillaReadingProgressionTimeLine?.updateTimelinePosition(readingPosition.getLocator());
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('READING_POSITION_UPDATED', {readingPositionData}));
            if (this.vanillaPublication.colibrioReaderPublication.getSourcePublication().getType() == PublicationType.EPUB){
                var contentUrl = (readingPosition.getReaderDocuments()[0] as IEpubReaderDocument).getSourceContentDocument().getContentUrl();
                let publication = (this.vanillaPublication.colibrioReaderPublication.getSourcePublication() as IEpubPublication);
                let resourceProvider = publication.getEpubResourceProvider();
                resourceProvider.fetch(contentUrl)
                .then((resp) => new Response(resp.getReadableStream()).text())
                .then((result) => {
                    try {
                        let xml = new XhtmlParser(result, contentUrl.toString(), false).getDocument();
                        const res =  xml?.evaluate("//xhtml:*[@epub:type]", xml.getRootNode(), function(prefix) { 
                            return xml?.lookupNamespaceURI(prefix==='xhtml' ? null : prefix)! 
                        });
                        let node = null;
                        let audioFound = false;
                        while (res != null && (node = res?.iterateNext())) {
                            if((node as HTMLElement).getAttribute("epub:type") == "ibooks:soundtrack"){
                                let audioTrack = (node as HTMLElement).getAttribute("src");
                                audioFound = true;
                                if (this.vanillaReaderView) {
                                    if (!this.ambientMediaPlayer){
                                        this.ambientMediaPlayer = new AmbientAudioMediaPlayer(this.vanillaPublication as VanillaReaderPublication);
                                    }
                                    this.ambientMediaPlayer.setCurrentlyPlaying(audioTrack!);
                                }
                            }
                        }    
                        if (!audioFound && this.ambientMediaPlayer){
                            this.ambientMediaPlayer.pause();
                            this.ambientMediaPlayer.setCurrentlyPlaying(undefined);
                            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('AMBIENT_AUDIO_NOT_FOUND', {}));
                        }    
                    } catch (e){
                        console.error("Unable to get publication info", e);
                    }     
                });
                
                
            }
            
        }

    };

    private _event_visibleRangeDataChanged = async () => {

        // Invalidate the current rects and update them based on the current visible content.
        await this.vanillaReaderView?.updateAndFetchVisibleContentRects();

        let visibleRangeData = await this.vanillaReaderView?.fetchVisibleRangeData(this.vanillaPublication?.navigationTree, this.vanillaReadingProgressionTimeLine);
        if (visibleRangeData) {
            if (this.vanillaReadingProgressionTimeLine?.isReady() && visibleRangeData.locator) {
                let wordRange: IIntegerRange | undefined = await this.vanillaReadingProgressionTimeLine?.fetchTimelineRange(visibleRangeData.locator);
                if (wordRange) {
                    this._registerWordRangeReadToStats(new IntRange(wordRange.start, wordRange.end));
                }
            }

            if (visibleRangeData.documentIsAtStart) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('DOCUMENT_IS_AT_START', {}));
            }
            if (visibleRangeData.documentIsAtEnd) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('DOCUMENT_IS_AT_END', {}));
            }

            // Check if the start / of the visible range is at the start or end of the publication, and if so send
            // events.

            if (visibleRangeData.publicationIsAtStart) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_IS_AT_START', {}));
            }
            if (visibleRangeData.publicationIsAtEnd) {
                VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_IS_AT_END', {}));
            }

            VanillaReaderEventBus.dispatchEvent(
                VanillaReaderEventBus.createEvent(
                    'VISIBLE_RANGE_DATA_CHANGED', {
                        visibleRangeData,
                    },
                ));
        }

    };

    private _event_textSelected = (ev: ISelectionChangedEngineEvent) => {

        // If we have a range of text selected we turn of view gestures in order to make sure that the view does respond
        // to swipe navigation events etc.
        if (ev.isRange) {
            this.vanillaReaderView?.setAllowedGestureTypes([]);
        } else {
            this.vanillaReaderView?.setAllowedGestureTypes([
                ReaderViewGestureType.SWIPE_NAVIGATION,
                ReaderViewGestureType.PAN_ZOOM,
            ]);
        }

        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('TEXT_SELECTED', {srcEventData: ev.toJSON()}));
    };

    private _event_appOnlineStateChanged: CustomEventListener<IVanillaReaderAppEvent_APP_ONLINE_STATE_CHANGED> = (_ev) => {
    };

    private _event_highlightAdded = ((ev: CustomEvent<IVanillaReaderAppEvent_HIGHLIGHT_ADDED>) => {
        if (!this.vanillaPublication) {
            return;
        }
        this.vanillaReaderView?.highlightsLayer?.addHighlight(ev.detail.highlight);
    });

    private _event_highlightUpdated = ((ev: CustomEvent<IVanillaReaderAppEvent_HIGHLIGHT_UPDATED>) => {
        if (!this.vanillaPublication) {
            return;
        }
        this.vanillaReaderView?.highlightsLayer?.updateHighlight(ev.detail.highlight);
    });

    private _event_highlightDeleted = ((ev: CustomEvent<IVanillaReaderAppEvent_HIGHLIGHT_DELETED>) => {
        if (!this.vanillaPublication) {
            return;
        }
        this.vanillaReaderView?.highlightsLayer?.deleteHighlight(ev.detail.locator);
    });

    private _event_bookmarkAdded = ((ev: CustomEvent<IVanillaReaderAppEvent_BOOKMARK_ADDED>) => {
        if (!this.vanillaPublication) {
            return;
        }
        this.vanillaReaderView?.bookmarksLayer?.addBookmark(ev.detail.bookmark);
    });

    private _event_bookmarkDeleted = ((ev: CustomEvent<IVanillaReaderAppEvent_BOOKMARK_DELETED>) => {
        if (!this.vanillaPublication) {
            return;
        }
        this.vanillaReaderView?.bookmarksLayer?.deleteBookmark(ev.detail.locator);
    });

}
